<div class="flex flex-col gap-2">
  <!-- Above whatever the search answered, and never inside a branch of it: a search that found
       nothing is exactly when the area needs widening, and the control has to still be there. -->
  @if (narrowable()) {
    <guarantees-search-area
      [area]="searchArea.area()"
      [radius]="searchArea.radiusMeters()"
      (placed)="searchArea.place($event)"
      (radiusPicked)="searchArea.setRadius($event)"
    ></guarantees-search-area>
  }

  @if (awaitingArea()) {
    <!-- Nothing has been asked of anyone: the map below is the question, not an answer to it. -->
    <p class="flex items-center gap-1.5 text-sm text-neutral-500">
      <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400">place</mat-icon>
      {{ "guaranteeV2.message.pickSearchArea" | translate }}
    </p>
  } @else if (loading()) {
    <!-- Said on the map where the map is up; only the list view needs a line of its own. -->
    @if (!showMap()) {
      <div class="flex items-center gap-2 px-2 py-3 text-sm text-neutral-400">
        <guarantees-spinner></guarantees-spinner>
        {{ "guaranteeV2.message.searchingSlots" | translate }}
      </div>
    }
  } @else if (failed()) {
    <guarantees-callout
      tone="warning"
      icon="cloud_off"
      stack
    >
      {{ "guaranteeV2.message.slotsUnavailable" | translate }}
      <button
        class="self-start rounded-full border border-warning-300 px-2 py-0.5 text-sm font-semibold text-warning-800 hover:bg-warning-100 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
        type="button"
        (click)="retry()"
      >
        {{ "guaranteeV2.action.retry" | translate }}
      </button>
    </guarantees-callout>
  } @else if (empty()) {
    <guarantees-callout
      tone="warning"
      icon="event_busy"
      stack
    >
      <span>{{ (familyDoctor() ? "guaranteeV2.message.noSlotsFamilyDoctor" : "guaranteeV2.message.noSlots") | translate }}</span>

      @if (narrowable()) {
        <span>{{ "guaranteeV2.message.widenSearchArea" | translate }}</span>
      }
    </guarantees-callout>
  } @else {
    <!-- Said rather than silently dropped: the hour taken earlier is no longer on offer. -->
    @if (choiceLapsed()) {
      <guarantees-callout
        tone="warning"
        icon="event_busy"
      >
        {{ "guaranteeV2.message.slotLapsed" | translate }}
      </guarantees-callout>
    }

    <!-- A partner network answers with everyone who offers the service; naming who is wanted is
         faster than scrolling past the rest. -->
    @if (filterable()) {
      <div class="flex flex-col gap-1.5">
        <guarantees-search-field
          icon="person_search"
          name="slotSearch"
          [ariaLabel]="'guaranteeV2.message.searchSlotOffers' | translate"
          [placeholder]="'guaranteeV2.message.searchSlotOffers' | translate"
          [value]="term()"
          (valueChange)="type($event)"
        ></guarantees-search-field>

        @if (offersSeen()) {
          <button
            guaranteesToggle
            shape="pill"
            size="xs"
            type="button"
            class="self-start"
            [pressed]="seenOnly()"
            (click)="toggleSeen()"
          >
            <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">history</mat-icon>
            {{ "guaranteeV2.action.onlySeenDoctors" | translate }}
          </button>
        }
      </div>
    }

    <!-- What the search left standing, and the one switch between its two readings — standing
         apart from the branches below, so it never jumps while a filter is being typed. -->
    <span class="flex items-center gap-2">
      <span class="min-w-0 flex-1 text-sm text-neutral-400">{{ "guaranteeV2.message.slotsFound" | translate: { count: matchCount() } }}</span>

      <!-- One set of offers, two ways of reading it: by who is free, or by where they are. -->
      @if (locatable()) {
        <span [class]="segmentedGroup">
          @for (option of views; track option.id) {
            <button
              guaranteesToggle
              type="button"
              size="sm"
              shape="segment"
              [pressed]="(option.id === 'map') === showMap()"
              (click)="view.set(option.id)"
            >
              <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">{{ option.icon }}</mat-icon>
              {{ option.labelKey | translate }}
            </button>
          }
        </span>
      }
    </span>

    <!-- The narrowing found nothing, which is not the partners having nothing: the box that was
         typed into stays where it is, with the way back to the whole list beside it. -->
    @if (noMatches()) {
      <guarantees-callout
        tone="warning"
        icon="search_off"
        stack
      >
        {{ "guaranteeV2.message.noSearchResults" | translate }}
        <button
          class="self-start rounded-full border border-warning-300 px-2 py-0.5 text-sm font-semibold text-warning-800 hover:bg-warning-100 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
          type="button"
          (click)="clearFilter()"
        >
          {{ "guaranteeV2.action.clearSearch" | translate }}
        </button>
      </guarantees-callout>
    } @else if (!showMap() && matchCount() > 0) {
      <ul class="flex max-h-96 flex-col gap-1.5 overflow-y-auto">
        @for (row of rows(); track row.offer.key) {
          <li [attr.data-offer]="row.offer.key">
            <div [class]="row.className">
              <!-- Folded, an offer is who could deliver it and either the soonest they could or what was taken. -->
              <button
                class="flex w-full items-start gap-2 px-2.5 py-1.5 text-left focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:cursor-not-allowed"
                type="button"
                [disabled]="!!row.refusalKey"
                (click)="toggle(row)"
              >
                @if (row.holdsChoice) {
                  <mat-icon class="mt-0.5 h-4! w-4! shrink-0 text-base! leading-4! text-primary-600">check_circle</mat-icon>
                } @else if (row.offer.logoUrl) {
                  <img
                    class="h-7 w-7 shrink-0 rounded-md border border-neutral-100 bg-neutral-0 object-contain p-0.5"
                    [class.opacity-40]="!!row.refusalKey"
                    [src]="row.offer.logoUrl"
                    alt=""
                    #offerLogo
                    (error)="offerLogo.hidden = true"
                  />
                } @else {
                  <mat-icon class="mt-0.5 h-4! w-4! shrink-0 text-base! leading-4! text-neutral-300">schedule</mat-icon>
                }

                <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span
                    class="min-w-0 truncate text-sm font-semibold"
                    [class]="row.refusalKey ? 'text-neutral-400' : 'text-neutral-700'"
                    >{{ row.offer.doctorName || row.offer.partnerName }}</span
                  >

                  @if (row.offer.doctorName) {
                    <span class="min-w-0 truncate text-sm text-neutral-500">{{ row.offer.partnerName }}</span>
                  }

                  <span class="flex min-w-0 items-center gap-1.5 text-sm text-neutral-400">
                    <span class="min-w-0 truncate">{{ row.offer.locationAddress || row.offer.locationName }}</span>

                    <!-- What the radius actually let through, which is the only way to read it back. -->
                    @if (row.distanceKm !== undefined) {
                      <span class="shrink-0 tabular-nums">{{ "guaranteeV2.label.distanceKm" | translate: { km: row.distanceKm } }}</span>
                    }
                  </span>

                  @if (row.offer.specializations?.length) {
                    <span class="min-w-0 truncate text-sm text-neutral-400">{{ row.offer.specializations?.join(", ") }}</span>
                  }

                  <!-- Said in the row rather than behind a hover: a row that cannot be clicked cannot be hovered. -->
                  @if (row.refusalKey) {
                    <span class="flex items-center gap-1 text-sm text-neutral-400">
                      <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">block</mat-icon>
                      {{ row.refusalKey | translate }}
                    </span>
                  }

                  <!-- What the partner knows about this doctor, and continuity first: seeing the same
                       person again is the whole of what a family doctor booking is for. -->
                  @if (row.offer.seenBefore || row.offer.recommended || row.offer.rating) {
                    <span class="flex flex-wrap items-center gap-x-2 text-sm">
                      @if (row.offer.seenBefore) {
                        <span class="flex items-center gap-0.5 text-success-600">
                          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">history</mat-icon>
                          {{ "guaranteeV2.message.doctorSeenBefore" | translate }}
                        </span>
                      }

                      @if (row.offer.recommended) {
                        <span class="flex items-center gap-0.5 text-primary-600">
                          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">verified</mat-icon>
                          {{ "guaranteeV2.message.doctorLiloPick" | translate }}
                        </span>
                      }

                      @if (row.offer.rating) {
                        <span class="flex items-center gap-0.5 text-neutral-400 tabular-nums">
                          <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">star</mat-icon>
                          {{ row.offer.rating }}
                          @if (row.offer.reviewCount) {
                            <span>({{ row.offer.reviewCount }})</span>
                          }
                        </span>
                      }
                    </span>
                  }
                </span>

                <span class="flex shrink-0 items-center gap-1">
                  <span
                    class="text-sm font-semibold tabular-nums"
                    [class]="row.chosenAt ? 'text-primary-700' : row.refusalKey ? 'text-neutral-400' : 'text-neutral-600'"
                    >{{ row.chosenAt || row.nearest | date: row.headerFormat }}</span
                  >

                  @if (!row.refusalKey) {
                    <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-300">{{ row.expanded ? "expand_less" : "expand_more" }}</mat-icon>
                  }
                </span>
              </button>

              @if (row.expanded) {
                <div class="flex flex-col gap-1.5 border-t border-neutral-100 px-2.5 py-2">
                  <ng-container *ngTemplateOutlet="slotTimes"></ng-container>
                </div>
              }
            </div>
          </li>
        }

        <!-- Inside the scroller, so going on with the list is where the list runs out. -->
        @if (hiddenCount()) {
          <li>
            <button
              class="w-full rounded-lg border border-dashed border-neutral-200 px-2 py-1.5 text-sm font-semibold text-neutral-500 transition-colors duration-150 hover:border-primary-300 hover:bg-primary-50/60 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
              type="button"
              (click)="showMore()"
            >
              {{ "guaranteeV2.action.showMoreOffers" | translate: { count: hiddenCount() } }}
            </button>
          </li>
        }
      </ul>
    }
  }

  <!-- The one map: the surface a place is taken on, and the surface the answers arrive on. It
       outlives every state above it, so a search that found nothing still shows where it looked. -->
  @if (mapLive()) {
    <div class="relative">
      <guarantees-location-map
        class="h-96"
        [named]="false"
        [shown]="showMap()"
        [pickable]="narrowable()"
        [markers]="markers()"
        [centre]="searchArea.area()"
        [preview]="searchArea.preview()"
        [selectedId]="markedBranch()"
        [busy]="loading() && !awaitingArea()"
        [busyLabel]="'guaranteeV2.message.searchingSlots' | translate"
        (selected)="chooseMarker($event)"
        (picked)="searchArea.placePoint($event)"
      ></guarantees-location-map>

      <!-- The pin taken, read where it was taken: who is there, how far, and the hours
           themselves — an hour is picked on the map, with no list to go and search through. -->
      @if (showMap()) {
        @if (focusedOffer(); as offer) {
          @let km = distanceKmOf(offer);

          <div class="absolute right-12 bottom-2 left-2 z-10 flex flex-col gap-1.5 rounded-xl border border-neutral-100 bg-neutral-0 p-2.5 shadow-lg">
            <div class="flex items-start gap-2">
              @if (offer.logoUrl) {
                <img
                  class="h-8 w-8 shrink-0 rounded-md border border-neutral-100 bg-neutral-0 object-contain p-0.5"
                  [src]="offer.logoUrl"
                  alt=""
                  #cardLogo
                  (error)="cardLogo.hidden = true"
                />
              }

              <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                <span class="min-w-0 truncate text-sm font-semibold text-neutral-700">{{ offer.doctorName || offer.partnerName }}</span>

                @if (offer.doctorName) {
                  <span class="min-w-0 truncate text-sm text-neutral-500">{{ offer.partnerName }}</span>
                }

                <span class="flex min-w-0 items-center gap-1.5 text-sm text-neutral-400">
                  <span class="min-w-0 truncate">{{ offer.locationAddress || offer.locationName }}</span>

                  @if (km !== undefined) {
                    <span class="shrink-0 tabular-nums">{{ "guaranteeV2.label.distanceKm" | translate: { km: km } }}</span>
                  }
                </span>
              </span>

              <button
                class="shrink-0 rounded-full p-0.5 text-neutral-400 transition-colors duration-150 hover:bg-neutral-100 hover:text-neutral-600 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
                type="button"
                [attr.aria-label]="'guaranteeV2.action.close' | translate"
                (click)="dismissCard()"
              >
                <mat-icon class="h-4! w-4! text-base! leading-4!">close</mat-icon>
              </button>
            </div>

            <div class="flex max-h-60 flex-col gap-1.5 overflow-x-hidden overflow-y-auto border-t border-neutral-100 pt-1.5">
              <ng-container *ngTemplateOutlet="slotTimes"></ng-container>
            </div>
          </div>
        }
      }
    </div>
  }
</div>

<!-- One answer, two places to give it: the same days and hours stand in an unfolded row and in
     the card over the map, and both read and write the same choice. -->
<ng-template #slotTimes>
  <!-- Where the strip stands, said once above it: the month costs a line in every cell
           and says the same thing fourteen times. -->
  <div class="flex items-baseline justify-between gap-2">
    <span class="text-xs font-semibold tracking-wide text-neutral-400 uppercase">{{ month() | date: "LLLL" }}</span>

    @if (!windowed()) {
      <span class="text-xs text-neutral-400 tabular-nums">{{ "guaranteeV2.message.freeTimesCount" | translate: { count: times().length } }}</span>
    }
  </div>

  <!-- A strip rather than a wrapped pile: a fortnight of days reads along one line. -->
  <div class="-mx-0.5 flex shrink-0 snap-x scroll-px-0.5 gap-1 overflow-x-auto px-0.5 pb-1">
    @for (entry of days(); track entry.key) {
      <button
        type="button"
        [attr.data-day]="entry.key"
        [class]="entry.key === day() ? daySelected : entry.slots.length ? dayOpen : dayEmpty"
        (click)="chooseDay(entry)"
      >
        <span class="text-xs leading-4 uppercase opacity-70">{{ entry.at | date: "EEE" }}</span>
        <span class="text-sm leading-4 font-semibold tabular-nums">{{ entry.at | date: "d" }}</span>
      </button>
    }
  </div>

  @if (windowed()) {
    <!-- A window is stated across the row: a lone chip reading "08:00" would look like
             an hour somebody picked, and nobody picked an hour here. -->
    <div class="flex flex-col gap-1">
      @for (slot of times(); track slot.startTime) {
        <button
          type="button"
          [class]="isChosen(slot) ? windowSelected : windowOpen"
          (click)="choose(slot)"
        >
          <mat-icon
            class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!"
            [class]="isChosen(slot) ? 'text-primary-600' : 'text-neutral-300'"
            >{{ isChosen(slot) ? "check_circle" : "schedule" }}</mat-icon
          >
          <span class="shrink-0 text-sm font-semibold text-neutral-700 tabular-nums">{{ slot.startTime | date: "HH:mm" }} – {{ slot.endTime | date: "HH:mm" }}</span>
          <span class="min-w-0 flex-1 truncate text-right text-xs text-neutral-400">{{ "guaranteeV2.label.visitWindow" | translate }}</span>
        </button>
      }
    </div>
  } @else if (times().length === 0) {
    <!-- A family doctor's calendar keeps the days they have nothing, because when
               they are next free is the question being asked. -->
    <span class="px-0.5 py-1 text-sm text-neutral-400">{{ "guaranteeV2.message.noTimesThatDay" | translate }}</span>
  } @else {
    <!-- Gathered into the parts of a day a clinic is actually spoken about, and left
             flat where there are too few hours for the sorting to sort anything. -->
    @for (group of groups(); track group.labelKey) {
      <div class="flex flex-col gap-1">
        @if (grouped()) {
          <span class="text-xs font-semibold tracking-wide text-neutral-400 uppercase">{{ group.labelKey | translate }}</span>
        }

        <div class="grid grid-cols-[repeat(auto-fill,minmax(3.75rem,1fr))] gap-1">
          @for (slot of group.slots; track slot.startTime) {
            <button
              type="button"
              [class]="isChosen(slot) ? timeSelected : timeOpen"
              (click)="choose(slot)"
            >
              @if (remote(slot)) {
                <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5!">videocam</mat-icon>
              }
              {{ slot.startTime | date: "HH:mm" }}
            </button>
          }
        </div>
      </div>
    }
  }
</ng-template>
